/**
 * Responsibility: keep a table inside the width it was given.
 *
 * Every table in this app is a kit `DataTable`, and the kit's scroller
 * allows horizontal overflow. That is the right default for a component
 * library and the wrong one here: a table you have to drag sideways hides
 * columns behind an edge with nothing to say they are there, and on a
 * phone it turns the whole page into something that slides under a thumb.
 *
 * The kit is a faithful copy of an upstream template and is not edited, so
 * this constrains it from outside. `table-layout: fixed` is the whole
 * trick: the browser then honours the declared column widths instead of
 * growing a column to fit its longest cell, and anything that does not fit
 * is clipped with an ellipsis rather than pushing the table wider.
 *
 * Columns with no declared width share what is left, which is why the name
 * column - the one that can take an ellipsis without losing its meaning -
 * is the one left undeclared in each table.
 */
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';

type FittedTableProps = {
  children: ReactNode;
  /** Measured by `useFillHeight`, so the table ends where the window does. */
  fillRef?: (node: HTMLElement | null) => void;
};

export function FittedTable({ children, fillRef }: FittedTableProps) {
  return (
    <Box
      ref={fillRef}
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        '& table': { tableLayout: 'fixed', width: '100%' },
        // Cells clip rather than widen. `minWidth: 0` is needed as well as
        // overflow: a flex child defaults to min-width:auto and would push
        // its cell open from the inside however the table is laid out.
        // The one exception: text gets an ellipsis rather than being cut
        // mid-letter. Headings are measured and should never reach it,
        // but one added later without measuring should read "IF ONE GE..."
        // rather than "IF ONE GEM".
        '& td, & th': { overflow: 'hidden', textOverflow: 'ellipsis' },
        '& td > *, & th > *': { minWidth: 0 },

        // **The whole header cell sorts, not just the words in it.**
        // The kit renders a sortable header as MUI's `TableSortLabel`,
        // an inline-flex span sized to its text, so the cell's padding
        // around it was dead. On a right-aligned column - Raw, Best
        // grade, Multiple - that dead zone is most of the cell, which
        // is why sorting those "did nothing" while the wide Card column
        // worked: people were clicking the header and missing the
        // target by a few pixels.
        //
        // Fixed from outside because the kit is a vendored copy. The
        // label is stretched to fill its cell and its contents pushed
        // to whichever side the column is aligned to, so nothing moves
        // on screen and every pixel of the header is clickable.
        '& thead th .MuiTableSortLabel-root': {
          width: '100%',
          justifyContent: 'flex-start',
        },
        '& thead th.MuiTableCell-alignRight .MuiTableSortLabel-root': {
          justifyContent: 'flex-end',
        },
        '& thead th.MuiTableCell-alignCenter .MuiTableSortLabel-root': {
          justifyContent: 'center',
        },

        // **A heading is one line or it is too long.** Wrapping was the
        // first answer to a clipped heading and it produced "SALES" set
        // one letter per line, reading vertically; allowing a break only
        // between words fixed that and left a header row two lines deep
        // with half its cells half empty. So headings do not wrap at
        // all, and the answer to one that does not fit is a shorter
        // heading or a wider column - both of which `tests/table-headers.mjs`
        // measures in a browser rather than leaving to the eye.
        '& thead th': {
          whiteSpace: 'nowrap',
          lineHeight: 1.2,
        },
      }}
    >
      {children}
    </Box>
  );
}
